/**
 * Identitatea vizuală a restaurantului: logo + copertă.
 *
 * De ce un card separat, deși pare „doar un upload": aceeași imagine ajunge în trei locuri
 * (antetul meniului public, centrul codului QR, ecranele digitale). Restaurantul trebuie să vadă
 * exact unde apare și cum arată, altfel uploadul rămâne o cutie neagră — iar un logo prost
 * încadrat se descoperă abia pe televizorul din sală.
 */
import { useRef, useState } from 'react';
import { Button, Card, CardHeader, Icon, InlineAlert, Select, cx } from '../components/ui.js';
import { api, uploadFile } from '../lib/api.js';
import { errorMessage, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';
import { useResource } from '../lib/hooks.js';

interface Asset {
  id: string;
  public_id: string;
  url: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  alt_text: string | null;
}

interface RestaurantRow {
  id: string;
  name: string;
  logo_asset_id: string | null;
}

const MAX_BYTES = 5 * 1024 * 1024;

export function BrandingCard() {
  const toast = useToast();
  const { can } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const restaurants = useResource<RestaurantRow[]>(() => api.get('/restaurants'));
  const assets = useResource<Asset[]>(() => api.get('/assets'));
  const [restaurantId, setRestaurantId] = useState<string>('');

  const restaurant = restaurants.data?.find((item) => item.id === restaurantId) ?? restaurants.data?.[0];
  const canUpload = can('asset.upload');
  const canUpdate = can('restaurant.update');

  async function handleFile(file: File | null | undefined) {
    if (!file) return;
    setError(null);

    // Verificările „ieftine" se fac în browser, ca utilizatorul să afle imediat ce nu merge;
    // serverul verifică oricum tipul real al fișierului, deci nu ne bazăm pe acestea.
    if (file.size > MAX_BYTES) {
      setError('Imaginea depășește 5 MB. Optimizeaz-o și încearcă din nou.');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Se acceptă PNG, JPEG sau WebP. (SVG nu se acceptă: ar putea conține cod.)');
      return;
    }

    setBusy(true);
    try {
      const created = await uploadFile<{ data: Asset }>(
        `/assets?filename=${encodeURIComponent(file.name)}&alt=${encodeURIComponent(restaurant?.name ?? '')}`,
        file,
      );
      await assets.reload();
      if (restaurant) await attach(created.data.id);
      toast.success('Logo încărcat', 'Apare în meniul public, pe codurile QR și pe ecrane.');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function attach(assetId: string | null) {
    if (!restaurant) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/restaurants/${restaurant.id}`, { logoAssetId: assetId });
      await restaurants.reload();
      toast.success(assetId ? 'Logo setat' : 'Logo eliminat', assetId ? 'Se aplică la următoarea publicare a meniului.' : undefined);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Identitate vizuală"
        subtitle="Logo-ul apare în meniul public, în centrul codurilor QR și pe ecranele din local."
      />

      {restaurants.loading || assets.loading ? (
        <p className="text-body text-ink-500">Se încarcă…</p>
      ) : (
        <div className="space-y-4">
          {restaurants.data && restaurants.data.length > 1 ? (
            <label className="block max-w-xs">
              <span className="mb-1.5 block text-caption font-medium text-ink-600">Restaurant</span>
              <Select value={restaurant?.id ?? ''} onChange={(event) => setRestaurantId(event.target.value)}>
                {restaurants.data.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}

          <div className="flex flex-wrap items-start gap-5">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border border-ink-100 bg-surface-2">
              {currentAsset(assets.data, restaurant?.logo_asset_id) ? (
                <img
                  src={currentAsset(assets.data, restaurant?.logo_asset_id)!.url}
                  alt="Logo actual"
                  className="h-full w-full object-contain p-2"
                />
              ) : (
                <Icon name="image" size={22} className="text-ink-300" />
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-3">
              <p className="text-body text-ink-600">
                PNG, JPEG sau WebP, până la 5 MB. Recomandat: pătrat, minim 512×512 px, fundal transparent.
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(event) => void handleFile(event.target.files?.[0])}
                />
                {canUpload ? (
                  <Button loading={busy} onClick={() => fileInput.current?.click()} icon={<Icon name="upload" size={16} />}>
                    Încarcă logo
                  </Button>
                ) : null}
                {restaurant?.logo_asset_id && canUpdate ? (
                  <Button variant="ghost" disabled={busy} onClick={() => void attach(null)}>
                    Elimină logo
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {error ? <InlineAlert tone="danger" title="Nu am putut salva">{error}</InlineAlert> : null}

          {assets.data?.length ? (
            <div>
              <p className="mb-2 text-caption font-medium text-ink-600">Din biblioteca contului</p>
              <div className="flex flex-wrap gap-2">
                {assets.data.slice(0, 12).map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    disabled={busy || !canUpdate}
                    onClick={() => void attach(asset.id)}
                    className={cx(
                      'h-16 w-16 overflow-hidden rounded-xl border bg-white p-1 transition-ui disabled:opacity-50',
                      asset.id === restaurant?.logo_asset_id ? 'border-brand ring-2 ring-brand/30' : 'border-ink-100 hover:border-ink-300',
                    )}
                    title={asset.alt_text ?? asset.mime_type}
                  >
                    <img src={asset.url} alt={asset.alt_text ?? 'Imagine'} className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <InlineAlert tone="info" title="Ce se întâmplă după încărcare">
            Logo-ul intră în codurile QR la descărcare și în meniul public după următoarea publicare. Codurile QR
            primesc automat nivelul maxim de corecție (H), ca logo-ul să nu afecteze citirea.
          </InlineAlert>
        </div>
      )}
    </Card>
  );
}

function currentAsset(assets: Asset[] | null | undefined, id: string | null | undefined): Asset | undefined {
  if (!assets || !id) return undefined;
  return assets.find((asset) => asset.id === id);
}
